import { Dispatch } from 'redux';
import { createAction } from '@reduxjs/toolkit';
import Services from 'services/network';
import { actionTypes } from 'modules/authentication/state/constants';
import TokenService from 'services/auth/TokenService';
import {
  ISignInAsSupportUserData,
  IUserAvatar,
  IUserProfileInfo,
} from 'modules/authentication/interfaces';
import { enqueueErrorNotification } from 'common/state/notifications/actions';
import { IServerError, ISuccessResponse } from 'common/interfaces/http';
import { batch } from 'react-redux';
import AppService from 'services/application/AppService';
import { Routes } from 'constants/route.constants';
import { GeneralThunkAction } from 'common/state/interfaces';
import { IUserOrganization } from 'common/interfaces/clients';
import { PermissionLevels } from 'common/constants/permissions';
import { IClubGeneralInfo } from 'modules/clubs/interfaces';
import { ICorporationGeneralInfo } from 'modules/corporations/interfaces';
import { resetSelectedEntranceIdAction } from 'modules/front-desk/state/actions';
import { INamedEntity } from 'common/interfaces/common';
import { resetUserRole } from 'common/state/userRole/actions';
import { fetchProfileFieldsInfoAction } from 'common/state/settings/actions';
import baseApi from 'api/baseApi';
import RouterService from 'services/application/RouterService';

const signInAction = createAction<ISuccessResponse>(actionTypes.USER_SIGN_IN);
const signInLoadingAction = createAction<boolean>(actionTypes.USER_SIGN_IN_LOADING);
export const signInErrorAction = createAction<IServerError | null>(actionTypes.USER_SIGN_IN_ERROR);

export const signIn = (
  email: string,
  password: string,
  corporationId: string,
  peakEmployee?: boolean,
): GeneralThunkAction => {
  return async dispatch => {
    dispatch(signInLoadingAction(true));

    try {
      const signInData = await Services.Authentication.signIn(
        email,
        password,
        corporationId,
        peakEmployee,
      );

      TokenService.setAccessToken(signInData.accessToken);
      TokenService.setRefreshToken(signInData.refreshToken);

      dispatch(signInAction({ success: true }));
    } catch (e) {
      dispatch(signInErrorAction(e));
    } finally {
      dispatch(signInLoadingAction(false));
    }
  };
};

const resetUserProfileAction = createAction<IUserProfileInfo | undefined>(
  actionTypes.RESET_USER_PROFILE_DATA,
);
export const clearUserDataAction = createAction(actionTypes.USER_LOG_OUT);

export const logOut = (): GeneralThunkAction => {
  return async dispatch => {
    try {
      await Services.Authentication.signOut();
    } catch (e) {
      dispatch(enqueueErrorNotification(e));
    } finally {
      TokenService.clearTokens();
      AppService.clearAppSelectedRegisters();
      AppService.clearAppSelectedEntrance();

      const privacyPolicyRoutes: string[] = [
        Routes.PRIVACY_POLICY,
        Routes.TERMS_OF_SERVICE,
        Routes.COOKIE_POLICY,
      ];

      if (privacyPolicyRoutes.some(item => item === window.location.pathname)) {
        RouterService.navigate(Routes.SIGN_IN);
      }

      batch(() => {
        dispatch(baseApi.util.resetApiState());
        dispatch(clearUserDataAction());
        dispatch(resetUserProfileAction());
        dispatch(resetSelectedEntranceIdAction());
        dispatch(resetUserRole());
        dispatch(fetchProfileFieldsInfoAction([]));
      });
    }
  };
};

const selectCurrentUserOrganizationAction = createAction<IUserOrganization>(
  actionTypes.SELECT_ORGANIZATION,
);
export const setOrganizationPrimaryColorAction = createAction<string>(
  actionTypes.SET_ORGANIZATION_PRIMARY_COLOR,
);
export const resetOrganizationPrimaryColorAction = createAction(
  actionTypes.RESET_ORGANIZATION_PRIMARY_COLOR,
);

export const selectCurrentUserOrganization = (userId: number, organization: IUserOrganization) => (
  dispatch: Dispatch,
): void => {
  AppService.setUserSelectedOrgId(userId, organization.id);
  dispatch(selectCurrentUserOrganizationAction(organization));
};

const fetchUserProfileAction = createAction<IUserProfileInfo>(actionTypes.FETCH_USER_PROFILE);
const fetchUserProfileLoadingAction = createAction<boolean>(actionTypes.FETCH_USER_PROFILE_LOADING);
const fetchUserProfileErrorAction = createAction<Error>(actionTypes.FETCH_USER_PROFILE_ERROR);
export const setUserAvatar = createAction<IUserAvatar>(actionTypes.SET_USER_AVATAR);

const chooseSelectedOrg = (
  user: IUserProfileInfo,
  prevSelectedOrgId?: string,
): IUserOrganization | null => {
  const { level, corporation, clubs } = user;

  if (level === PermissionLevels.PEAK) {
    return null;
  }

  const selectedClub = clubs.find(club => club.id === prevSelectedOrgId);

  if (selectedClub) {
    return { ...selectedClub, permissionsLevel: PermissionLevels.CLIENT };
  }

  if (level === PermissionLevels.CORPORATE) {
    return { ...corporation, permissionsLevel: PermissionLevels.CORPORATE };
  }

  if (clubs.length) {
    return { ...clubs[0], permissionsLevel: PermissionLevels.CLIENT };
  }

  return null;
};

export const fetchUserProfile = (): GeneralThunkAction => {
  return async dispatch => {
    dispatch(fetchUserProfileLoadingAction(true));
    try {
      const userProfile: IUserProfileInfo = await Services.Authentication.getProfile();

      const prevSelectedOrgId: string = AppService.getUserSelectedOrgId(userProfile.id);
      const selectedOrg = chooseSelectedOrg(userProfile, prevSelectedOrgId);

      if (selectedOrg) {
        AppService.setUserSelectedOrgId(userProfile.id, selectedOrg.id);
        batch(() => {
          dispatch(selectCurrentUserOrganizationAction(selectedOrg));
          dispatch(fetchUserProfileAction(userProfile));
        });
      } else {
        dispatch(fetchUserProfileAction(userProfile));
      }

      const { avatar, preferredName } = userProfile;
      dispatch(setUserAvatar({ avatar, preferredName }));
    } catch (e) {
      dispatch(enqueueErrorNotification(e));
      dispatch(fetchUserProfileErrorAction(e));
    } finally {
      dispatch(fetchUserProfileLoadingAction(false));
    }
  };
};

const fetchCorporationsForSignInAction = createAction<INamedEntity[]>(
  actionTypes.FETCH_CORPORATIONS_FOR_SIGN_IN,
);
const fetchCorporationsForSignInActionLoading = createAction<boolean>(
  actionTypes.FETCH_CORPORATIONS_FOR_SIGN_IN_LOADING,
);
export const resetCorporationsForSignInAction = createAction(
  actionTypes.RESET_CORPORATIONS_FOR_SIGN_IN,
);

export const fetchCorporationsForSignIn = (email: string): GeneralThunkAction => {
  return async dispatch => {
    dispatch(fetchCorporationsForSignInActionLoading(true));

    try {
      const corporations = await Services.Authentication.getCorporationsForSignIn(email);

      dispatch(fetchCorporationsForSignInAction(corporations));
    } catch (error) {
      dispatch(enqueueErrorNotification(error));
    } finally {
      dispatch(fetchCorporationsForSignInActionLoading(false));
    }
  };
};

export const updateClubInProfile = createAction<IClubGeneralInfo>(actionTypes.UPDATE_CLUB_DATA);
export const updateCorporationInProfile = createAction<ICorporationGeneralInfo>(
  actionTypes.UPDATE_CORPORATION_DATA,
);

export const setSignInAsSupportUserData = createAction<ISignInAsSupportUserData>(
  actionTypes.SET_SIGN_IN_AS_SUPPORT_USER_DATA,
);
export const resetSignInAsSupportUserData = createAction(
  actionTypes.RESET_SIGN_IN_AS_SUPPORT_USER_DATA,
);
